/**
 * <s-dock>: mobile bottom menu bar that stays tucked away while the header is on
 * screen and slides up once the header scrolls out of view (and back down when the
 * shopper returns to the top). On product pages it also stays away while the sticky
 * Add to cart card is showing: <s-sticky-atc> fires `sticky-atc:change` with
 * detail.visible, and only after the card has landed in the main button does the
 * dock slide in. While hidden it is inert so its links can't be focused or tapped.
 */
class SDock extends HTMLElement {
  #observer;
  #timer = 0;
  #headerVisible = true;
  #stickyVisible = false;
  #onSticky = (event) => {
    this.#stickyVisible = Boolean(event.detail?.visible);
    this.#render();
  };

  connectedCallback() {
    document.addEventListener('sticky-atc:change', this.#onSticky);
    this.#spinRim();
    const header = document.querySelector('header') || document.querySelector('[data-header-nav-group]');
    if (!header || !('IntersectionObserver' in window)) {
      this.#headerVisible = false;
      this.#render();
      return;
    }

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#headerVisible = entry.isIntersecting;
      this.#render();
    });
    this.#observer.observe(header);
  }

  // Sends a soft gold comet of light around the dock edge ([data-dock-rim]); skipped with reduced motion.
  #spinRim() {
    const rim = this.querySelector('[data-dock-rim]');
    if (!rim || this.rimSpinning || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    try {
      CSS.registerProperty({ name: '--dock-angle', syntax: '<angle>', inherits: false, initialValue: '200deg' });
    } catch {
      // Already registered.
    }
    this.rimSpinning = true;
    rim.animate([{ '--dock-angle': '0deg' }, { '--dock-angle': '360deg' }], { duration: 5200, iterations: Infinity });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    document.removeEventListener('sticky-atc:change', this.#onSticky);
    clearTimeout(this.#timer);
  }

  #render() {
    // Settle briefly before changing, so quick back-to-back signals (the sticky
    // card re-rendering on a variant change, the header edge crossing) don't make
    // the bar flash in and out.
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      const hidden = this.#headerVisible || this.#stickyVisible;
      if (hidden === this.hasAttribute('data-hidden')) return;
      this.toggleAttribute('data-hidden', hidden);
      this.inert = hidden;
    }, 250);
  }
}

if (!customElements.get('s-dock')) customElements.define('s-dock', SDock);
